import * as Yup from "yup";
import YupPassword from "yup-password";
import { FieldMetaData, Fields } from "../types/SchemaFactory";
import { TFunction } from "i18next";
import { QUILL_EMPTY_STRING } from "../domain/constants";

YupPassword(Yup);

/**
 * Check if the object has at least one meaningful key with a meaningful value.
 *
 * For strings, checks for non-empty and not equal to `QUILL_EMPTY_STRING`,
 * because Quill editor outputs that value for empty content.
 *
 * Use for validating object arrays where empty objects should be ignored in validation.
 *
 * @returns `true` if the object has at least one meaningful data.
 */
const isObjectMeaningful = (obj: any, meaningfulKeys: string[]): boolean => {
    if (!obj || typeof obj !== "object") return false;
    return meaningfulKeys.some((key) => {
        const v = obj[key];
        if (typeof v === "string")
            return v !== QUILL_EMPTY_STRING && v.trim().length > 0;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === "number") return !isNaN(v);
        if (typeof v === "boolean") return true;
        return false;
    });
};

export function schemaFactory<T extends Object>(
    t: TFunction,
    fields: Fields<T>
) {
    return Yup.object().shape(
        Object.entries(fields).reduce((acc, [key, rawValue]) => {
            const value = rawValue as FieldMetaData;
            let schema:
                | Yup.StringSchema
                | Yup.NumberSchema
                | Yup.BooleanSchema
                | Yup.MixedSchema<any>
                | Yup.ArraySchema<any, any, any>
                | Yup.ObjectSchema<any> = Yup.string();
            if (value.type === "number") {
                schema = Yup.number();
            } else if (value.type === "select") {
                schema = Yup.mixed().oneOf(
                    value.selectOptions?.map((option) => option.value) ?? [],
                    t("errors.validation.invalidOption")
                );
            } else if (value.type === "checkbox") {
                schema = Yup.boolean();
            } else if (value.type === "email") {
                schema = Yup.string().email(
                    t("errors.validation.invalidEmail")
                );
            } else if (value.type === "password") {
                schema = Yup.string()
                    .password()
                    .minSymbols(1, t("errors.validation.password.minSymbols"))
                    .minLowercase(
                        1,
                        t("errors.validation.password.minLowercase")
                    )
                    .minUppercase(
                        1,
                        t("errors.validation.password.minUppercase")
                    )
                    .minNumbers(1, t("errors.validation.password.minNumbers"));
            } else if (value.type === "files") {
                schema = Yup.array().of(Yup.mixed());
            } else if (value.pattern) {
                schema = Yup.string().matches(
                    value.pattern,
                    t(value.patternErrorKey)
                );
            } else if (value.objectArray) {
                const innerObjectSchema = schemaFactory(t, value.objectArray);

                /**
                 * Filtered keys without "hidden" type fields (these are not filled by the user, set programmatically)
                 */
                const meaningfulKeys = Object.entries(value.objectArray)
                    .filter(([_, { type }]) => type !== "hidden")
                    .map(([key]) => key);

                /**
                 * Schema for each element in the array, which make the element optional if it has no meaningful data.
                 * Used for ignoring in validation the empty objects in the array.
                 */
                const elementSchema = Yup.lazy((val: any) => {
                    if (!isObjectMeaningful(val, meaningfulKeys)) {
                        return Yup.mixed().notRequired().nullable();
                    }
                    return innerObjectSchema;
                });

                /**
                 * Final schema for the array, which tests that at least one element has meaningful data (at least one element is valid).
                 */
                schema = Yup.array()
                    .of(elementSchema)
                    .test(
                        "at-least-one-valid",
                        t("errors.validation.atLeastOneValidElement"),
                        (arr) =>
                            Array.isArray(arr) &&
                            arr.some((el) =>
                                isObjectMeaningful(el, meaningfulKeys)
                            )
                    );
            }

            if (
                schema instanceof Yup.StringSchema ||
                schema instanceof Yup.NumberSchema
            ) {
                if (value.min) {
                    schema = (schema as any).min(
                        value.min,
                        t("errors.validation.min") + value.min
                    );
                }
                if (value.max) {
                    schema = (schema as any).max(
                        value.max,
                        t("errors.validation.max") + value.max
                    );
                }
            }

            schema = value.optional
                ? schema.optional().nullable()
                : schema.required(t("errors.validation.required"));

            if (value.array && !value.objectArray) {
                schema = Yup.array().of(schema);
            }

            acc[key] = schema;
            return acc;
        }, {} as Yup.ObjectShape)
    );
}
